import { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";

const HERO_QUOTES = [
  "Still Iconic. Just Healing.",
  "Injured, Not Invisible.",
  "Heal Loud.",
];

const EASE = [0.16, 1, 0.3, 1];

const IMG_A = "/renders/hero-a4.png";
const IMG_SHOULDER = "/renders/lineup-shoulder2.png";
const IMG_ELBOW = "/renders/lineup-elbow2.png";
const IMG_ANKLE = "/renders/lineup-ankle2.png";

const MaskedLines = ({ text, baseDelay = 0 }) => {
  const lines = text.split(/(?<=\.)\s+/).filter(Boolean);
  return (
    <>
      {lines.map((line, i) => (
        <span key={line} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
          <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            exit={{ y: "-115%" }}
            transition={{ duration: 0.8, delay: baseDelay + i * 0.12, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </>
  );
};

const ActCopy = ({ index, name, quote, opacity, y, x, testId }) => (
  <div className="absolute left-8 md:left-14 top-1/2 -translate-y-1/2 text-left">
    <motion.div data-testid={testId} style={{ opacity, y, x }}>
      <p className="text-[10px] md:text-xs font-medium tracking-[0.3em] uppercase text-[#EFE8DC]/60 mb-5">
        The Lineup — {index}
      </p>
      <h2 className="font-display text-4xl md:text-7xl tracking-tighter leading-[0.95] text-[#EFE8DC]">
        {name}
      </h2>
      <p className="mt-4 font-display italic text-xl md:text-3xl text-[#EFE8DC]/85">
        "{quote}"
      </p>
      <p className="mt-5 text-[10px] md:text-xs font-medium tracking-[0.25em] uppercase text-[#EFE8DC]/55">
        Coming Soon
      </p>
    </motion.div>
  </div>
);

export const Hero = ({ onJoin }) => {
  const ref = useRef(null);
  const [quoteIndex, setQuoteIndex] = useState(() =>
    Math.floor(Math.random() * HERO_QUOTES.length)
  );
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const imgAOpacity = useTransform(scrollYProgress, [0.1, 0.18], [1, 0]);
  const shoulderOpacity = useTransform(scrollYProgress, [0.1, 0.18, 0.3, 0.38], [0, 1, 1, 0]);
  const elbowOpacity = useTransform(scrollYProgress, [0.3, 0.38, 0.48, 0.56], [0, 1, 1, 0]);
  const ankleOpacity = useTransform(scrollYProgress, [0.48, 0.56], [0, 1]);
  const headOpacity = useTransform(scrollYProgress, [0.08, 0.14], [1, 0]);
  const headY = useTransform(scrollYProgress, [0.08, 0.14], [0, -60]);
  const shoulderTextOpacity = useTransform(scrollYProgress, [0.2, 0.26, 0.3, 0.34], [0, 1, 1, 0]);
  const shoulderTextY = useTransform(scrollYProgress, [0.2, 0.26, 0.3, 0.34], [30, 0, 0, -30]);
  const elbowTextOpacity = useTransform(scrollYProgress, [0.39, 0.45, 0.49, 0.53], [0, 1, 1, 0]);
  const elbowTextY = useTransform(scrollYProgress, [0.39, 0.45, 0.49, 0.53], [30, 0, 0, -30]);
  const ankleTextOpacity = useTransform(scrollYProgress, [0.57, 0.63, 0.67, 0.71], [0, 1, 1, 0]);
  const ankleTextY = useTransform(scrollYProgress, [0.57, 0.63, 0.67, 0.71], [30, 0, 0, -30]);
  const endOpacity = useTransform(scrollYProgress, [0.75, 0.83], [0, 1]);
  const endY = useTransform(scrollYProgress, [0.75, 0.83], [24, 0]);

  // Subtle mouse parallax across the pinned stage
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const counterX = useTransform(sx, (v) => -v * 0.45);
  const handleMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 16);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  const handleMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  // Magnetic join button
  const btnX = useMotionValue(0);
  const btnY = useMotionValue(0);
  const btnSX = useSpring(btnX, { stiffness: 180, damping: 14 });
  const btnSY = useSpring(btnY, { stiffness: 180, damping: 14 });
  const handleBtnMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    btnX.set((e.clientX - (r.left + r.width / 2)) * 0.25);
    btnY.set((e.clientY - (r.top + r.height / 2)) * 0.35);
  };
  const resetBtn = () => {
    btnX.set(0);
    btnY.set(0);
  };

  useEffect(() => {
    const timer = setInterval(
      () => setQuoteIndex((i) => (i + 1) % HERO_QUOTES.length),
      4000
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[550vh] px-3 md:px-6"
    >
      <div
        className="sticky top-0 h-screen min-h-[600px] rounded-3xl overflow-hidden bg-[#C7B198]"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <motion.div style={{ x: sx, y: sy, scale: imgScale }} className="absolute inset-0">
          <motion.img
            data-testid="hero-image"
            src={IMG_A}
            alt="Curvy athlete in mocha set with hip-forward pose, wearing a LIGATEN knee brace printed Heal Loud"
            style={{ opacity: imgAOpacity }}
            className="absolute inset-0 w-full h-full object-cover object-[50%_35%] scale-[1.06]"
          />
          <motion.div style={{ opacity: shoulderOpacity }} className="absolute inset-0">
            <img
              data-testid="hero-image-shoulder"
              src={IMG_SHOULDER}
              alt="Model with sleek straight hair wearing the LIGATEN shoulder brace printed Recovery Era"
              className="w-full h-full object-cover object-[72%_50%] scale-[1.06] md:object-[68%_30%]"
            />
          </motion.div>
          <motion.div style={{ opacity: elbowOpacity }} className="absolute inset-0">
            <img
              data-testid="hero-image-elbow"
              src={IMG_ELBOW}
              alt="Model wearing the LIGATEN elbow sleeve printed Still Iconic"
              className="w-full h-full object-cover object-[72%_50%] scale-[1.06] md:object-[68%_30%]"
            />
          </motion.div>
          <motion.div style={{ opacity: ankleOpacity }} className="absolute inset-0">
            <img
              data-testid="hero-image-ankle"
              src={IMG_ANKLE}
              alt="Model wearing the LIGATEN ankle brace printed Torn Not Broken"
              className="w-full h-full object-cover object-[72%_50%] scale-[1.06] md:object-[68%_45%]"
            />
          </motion.div>
        </motion.div>
        <div className="absolute inset-0 bg-[#2C241E]/40" />
        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: EASE }}
          className="absolute top-20 right-8 md:top-20 md:right-14 text-[9px] md:text-[11px] font-medium tracking-[0.3em] uppercase text-[#EFE8DC]/60 text-right leading-relaxed"
        >
          Crafted by a<br />
          Physiotherapist in<br />
          Australia
        </motion.p>
        <motion.div
          style={{ opacity: headOpacity, y: headY, x: counterX }}
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
        >
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-[#EFE8DC]/50" />
            <p
              data-testid="hero-overline"
              className="text-[10px] md:text-xs font-medium tracking-[0.3em] uppercase text-[#EFE8DC]/70"
            >
              Est. 2026 — Melbourne
            </p>
            <span className="h-px w-10 bg-[#EFE8DC]/50" />
          </motion.div>
          <AnimatePresence mode="wait">
            <motion.h1
              key={quoteIndex}
              data-testid="hero-headline"
              className="font-display text-[#EFE8DC] text-5xl sm:text-6xl md:text-8xl tracking-tighter leading-[0.95] max-w-5xl"
            >
              <MaskedLines text={HERO_QUOTES[quoteIndex]} baseDelay={0.2} />
            </motion.h1>
          </AnimatePresence>
          <motion.p
            data-testid="hero-subline"
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="mt-6 text-sm md:text-base leading-relaxed text-[#EFE8DC]/75 max-w-md"
          >
            A new way to move through recovery. Founded by a physiotherapist. Coming soon.
          </motion.p>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          >
            <motion.div
              style={{ x: btnSX, y: btnSY }}
              onMouseMove={handleBtnMove}
              onMouseLeave={resetBtn}
              className="inline-block"
            >
              <button
                data-testid="hero-join-waitlist-button"
                onClick={onJoin}
                className="mt-10 rounded-full bg-[#EFE8DC] text-[#2C241E] text-xs md:text-sm font-medium tracking-[0.2em] uppercase px-9 py-4 transition-[background-color,box-shadow] duration-200 hover:bg-[#F3F1EB] hover:shadow-xl"
              >
                Join the List
              </button>
            </motion.div>
            <p
              data-testid="hero-quote-hint"
              className="mt-6 text-[10px] md:text-xs font-medium tracking-[0.25em] uppercase text-[#EFE8DC]/55"
            >
              Six stitched lines. One is yours — soon.
            </p>
          </motion.div>
        </motion.div>
        <ActCopy
          testId="hero-copy-shoulder"
          index="01"
          name="Shoulder"
          quote="Recovery Era."
          opacity={shoulderTextOpacity}
          y={shoulderTextY}
          x={counterX}
        />
        <ActCopy
          testId="hero-copy-elbow"
          index="02"
          name="Elbow"
          quote="Still Iconic."
          opacity={elbowTextOpacity}
          y={elbowTextY}
          x={counterX}
        />
        <ActCopy
          testId="hero-copy-ankle"
          index="03"
          name="Ankle"
          quote="Torn, Not Broken."
          opacity={ankleTextOpacity}
          y={ankleTextY}
          x={counterX}
        />
        <motion.p
          data-testid="hero-end-caption"
          style={{ opacity: endOpacity, y: endY }}
          className="absolute inset-x-0 bottom-14 text-center font-display italic text-2xl md:text-4xl text-[#EFE8DC]/90"
        >
          Recovery, Reimagined.
        </motion.p>
      </div>
    </section>
  );
};
